import React from 'react';
import { Phone } from 'lucide-react';
import {
  Input,
  Label,
  Card,
  CardContent,
  CardHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { formatUsPhoneEntry, isCompleteUsPhone } from '../../utils/intake';
import { US_STATES } from '../../constants/options';
import {
  MAX_LENGTHS,
  PHONE_ERROR_MESSAGE,
  type PatientInfoSectionProps,
} from './patientInfoForm';

// The patient's own phone, email and address. Controlled by PatientInfoTab,
// which owns the form state and autosave.
export const PatientContactSection: React.FC<PatientInfoSectionProps> = ({
  form,
  updateField,
  canEditPatientInfo,
}) => {
  return (
    <Card>
      <CardHeader className='pb-4'>
        <div className='flex items-center gap-2'>
          <Phone className='h-4 w-4 text-muted-foreground' />
          <h3 className='text-sm font-semibold'>Contact Information</h3>
        </div>
      </CardHeader>
      <CardContent>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div className='space-y-1.5'>
            <Label htmlFor='phone'>Phone</Label>
            <Input
              id='phone'
              value={form.phone}
              onChange={e =>
                updateField('phone', formatUsPhoneEntry(e.target.value))
              }
              disabled={!canEditPatientInfo}
              inputSize='sm'
              aria-invalid={!isCompleteUsPhone(form.phone) || undefined}
              aria-describedby={
                !isCompleteUsPhone(form.phone) ? 'phone-error' : undefined
              }
            />
            {!isCompleteUsPhone(form.phone) && (
              <p
                id='phone-error'
                className='mt-1 text-xs text-destructive'
                role='alert'
              >
                {PHONE_ERROR_MESSAGE}
              </p>
            )}
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='email'>Email</Label>
            <Input
              id='email'
              type='email'
              value={form.email}
              onChange={e => updateField('email', e.target.value)}
              disabled={!canEditPatientInfo}
              inputSize='sm'
            />
          </div>
          <div className='md:col-span-3 space-y-1.5'>
            <Label htmlFor='address'>Street Address</Label>
            <Input
              id='address'
              value={form.address}
              onChange={e => updateField('address', e.target.value)}
              disabled={!canEditPatientInfo}
              inputSize='sm'
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='city'>City</Label>
            <Input
              id='city'
              value={form.city}
              onChange={e => updateField('city', e.target.value)}
              disabled={!canEditPatientInfo}
              inputSize='sm'
            />
          </div>
          <div className='space-y-1.5'>
            <Label>State</Label>
            <Select
              value={form.state}
              onValueChange={v => updateField('state', v)}
            >
              <SelectTrigger selectSize='sm' disabled={!canEditPatientInfo}>
                <SelectValue placeholder='Select state' />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map(st => (
                  <SelectItem key={st.value} value={st.value}>
                    {st.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='zip_code'>ZIP Code</Label>
            <Input
              id='zip_code'
              value={form.zip_code}
              onChange={e => updateField('zip_code', e.target.value)}
              disabled={!canEditPatientInfo}
              inputSize='sm'
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='county'>County</Label>
            <Input
              id='county'
              value={form.county}
              onChange={e => updateField('county', e.target.value)}
              disabled={!canEditPatientInfo}
              maxLength={MAX_LENGTHS.county}
              inputSize='sm'
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
